import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { formatDashSeparator } from 'LuxKit/intl/separators'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import PlaceImageLoader from 'tripPlanner/components/Chat/PlaceImageLoader'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { isExperienceSavedData, isHotelSavedData, isSppSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { formatStopDateRange } from 'tripPlanner/utils/chatDates'
import TextButton from 'LuxKit/Button/TextButton'
import DayItineraryCanvas from './DayItineraryCanvas'
import ChatItemDropdown from './ChatItemDropdown'
import noop from 'lib/function/noop'
import ModalContext from 'contexts/ModalContext'
import ConfirmDeleteStopModal from './ConfirmDeleteStopModal'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Counter, { type CounterChangeEventHandler } from 'LuxKit/Counter'
import ItineraryLoadingState from './ItineraryLoadingState'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import {
  type HotelMapViewData,
  type ExperienceMapViewData,
  type TripItineraryData,
  type ManualItineraryDeleteRequest,
  type TripItineraryItem,
  type TripItemWithoutDay,
} from 'tripPlanner/api/types'
import PlaceInfoDrawer from './PlaceInfoDrawer'
import { HotelListingDrawer } from './HotelListingDrawer'
import type { DrawerHotel } from './HotelListingDrawer'
import { ExperienceListingDrawer } from './ExperienceListingDrawer'
import ChatOfferDetailDrawer from './ChatOfferDetailDrawer'
import ChatExperienceDetailDrawer from './ChatExperienceDetailDrawer'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { useChatStreamStatus } from '../contexts/ChatStreamStatusContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import type { DrawerExperience } from './ExperienceListingDrawer/ExperienceDetailView'
import type { OfferClickSearchParams } from '../contexts/ChatMessageActionsContext'
import { useSortedHighlights } from '../hooks/useSortedHighlights'
import type { Highlight } from './TripPlanCanvas'
import type { MapCoordinate } from 'tripPlanner/utils/mapMarkerTransforms'
import type { LEExploreCategorySource } from 'tripPlanner/pages/chat/chatExploreCategories'
import TripPlanHotelTile from './TripPlanHotelTile'
import useTripPlanHotelTileData from '../hooks/useTripPlanHotelTileData'
import useTripPlanExperienceTileData from '../hooks/useTripPlanExperienceTileData'
import {
  StopSheet,
  StopSheetContent,
  ImageContainer,
  StopMainContent,
  StopHeaderChevronButton,
  StopHeaderChevron,
  StopLearnMoreButton,
  StopDetailsPanel,
  StopDetailsContent,
  StopTitleContent,
  ClickableHeading,
  ClickableHighlightTitle,
} from './TripPlanCanvasItem.styles'
import TripPlanExperienceTile from './TripPlanExperienceTile'

interface Props {
  data?: TripItineraryData
  stopId: number
  placeName: string
  placeCountry: string
  numberOfDays: number
  description: string
  imageId?: string
  stopSeq: number
  highlights?: Array<Highlight>
  googlePlaceId?: string
  onLearnMoreClick?: (data: { googlePlaceId: string; placeName: string; countryName: string; stopId?: number }) => void
  onSendMessage?: (message: string) => void
  onDeleteTarget?: (target: ManualItineraryDeleteRequest) => Promise<void> | void
  onExploreMap: (coordinates: MapCoordinate, source: LEExploreCategorySource) => void
  onShowMap: (data: HotelMapViewData) => void
  onShowExperienceOnMap?: (data: ExperienceMapViewData) => void
  stopCoordinates?: MapCoordinate
  isEnriching?: boolean
  onAskAI?: (dayNumber: number, dayTitle: string, date?: string) => void
  onAskAIForItem?: (item: TripItineraryItem | TripItemWithoutDay) => void
  scrollTargetDayId?: number
  scrollTargetItemId?: number
}

function TripPlanCanvasItem({
  data,
  stopId,
  placeName,
  placeCountry,
  numberOfDays,
  imageId,
  highlights,
  googlePlaceId,
  onLearnMoreClick = noop,
  onSendMessage = noop,
  onDeleteTarget,
  onExploreMap,
  onShowMap,
  onShowExperienceOnMap,
  stopCoordinates,
  isEnriching = false,
  onAskAI,
  onAskAIForItem,
  scrollTargetDayId,
  scrollTargetItemId,
}: Props) {
  const intl = useIntl()
  const { isStreaming, isStreamOpen } = useChatStreamStatus()
  const { isManualItineraryDeletePending } = useItineraryContext()
  const deleteActionsDisabled = isStreaming || isStreamOpen || isManualItineraryDeletePending
  const [isStopExpanded, setIsStopExpanded] = useState(true)
  const [isCreateItineraryOpen, setIsCreateItineraryOpen] = useState(false)
  const [itineraryDays, setItineraryDays] = useState<number>(1)
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false)
  const [requestedDaysCount, setRequestedDaysCount] = useState<number>(0)
  const [isHotelDrawerOpen, setIsHotelDrawerOpen] = useState<boolean>(false)
  const [isExperienceDrawerOpen, setIsExperienceDrawerOpen] = useState<boolean>(false)
  const [selectedHighlight, setSelectedHighlight] = useState<{ googlePlaceId: string; name: string; itemId: number } | undefined>(undefined)
  const [selectedHotelHighlight, setSelectedHotelHighlight] = useState<{
    offerId: string
    accommodationPropertyId?: string
    offerType?: string
    searchParams?: OfferClickSearchParams
  } | undefined>(undefined)
  const createItineraryTriggerRef = useRef<HTMLButtonElement>(null)

  const showModal = useContext(ModalContext)

  const currentStop = useMemo(() => {
    return data?.stops.find(stop => stop.id === stopId)
  }, [data, stopId])

  const {
    bookedHotelSummary,
    hasSavedHotels,
    hotelTilePrice,
    savedHotelCount,
  } = useTripPlanHotelTileData({
    currentStop,
  })

  const {
    bookedExperienceSummary,
    bookedExperienceCount,
    hasSavedExperiences,
    experienceTilePrice,
    savedExperienceCount,
  } = useTripPlanExperienceTileData({
    currentStop,
  })

  const actualDaysCount = currentStop?.days?.length ?? 0
  const hasDays = actualDaysCount > 0
  const hasItemsWithoutDay = (currentStop?.itemsWithoutDay?.length ?? 0) > 0
  const hasHighlights = (highlights?.length ?? 0) > 0

  useEffect(() => {
    if (isGeneratingItinerary && hasDays) {
      setIsGeneratingItinerary(false)
    }
  }, [isGeneratingItinerary, hasDays])

  useEffect(() => {
    if (isGeneratingItinerary && !isStreaming && !hasDays) {
      setIsGeneratingItinerary(false)
    }
  }, [isGeneratingItinerary, isStreaming, hasDays])

  useEffect(() => {
    if (requestedDaysCount > 0 && hasDays && !isGeneratingItinerary) {
      setRequestedDaysCount(0)
    }
  }, [hasDays, isGeneratingItinerary, requestedDaysCount])

  const toggleStopExpanded = useCallback(() => {
    setIsStopExpanded(prev => !prev)
  }, [])

  const onLearnMoreClickHandler = useCallback(() => {
    if (googlePlaceId) {
      onLearnMoreClick({ googlePlaceId, placeName, countryName: placeCountry, stopId })
    }
  }, [onLearnMoreClick, googlePlaceId, placeName, placeCountry, stopId])

  const toggleCreateItineraryDropdown = useCallback(() => {
    setIsCreateItineraryOpen(prev => !prev)
  }, [])

  const closeCreateItineraryDropdown = useCallback(() => {
    setIsCreateItineraryOpen(false)
  }, [])

  const onItineraryDaysChange = useCallback<CounterChangeEventHandler>((value) => {
    setItineraryDays(value)
  }, [])

  const handleCreateItinerary = useCallback((days?: number) => {
    const daysToUse = days ?? itineraryDays
    closeCreateItineraryDropdown()
    setIsGeneratingItinerary(true)
    setRequestedDaysCount(daysToUse)
    const displayDaysLabel = intl.formatMessage(
      {
        id: 'WuQGcs5pleJG0thhNUz_',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
        description: 'Day count for itinerary creation prompt on the itinerary or schedule view',
      },
      { count: daysToUse },
    )
    const contextDaysLabel = daysToUse === 1 ? '1 day' : `${daysToUse} days`
    const displayMessage = intl.formatMessage(
      {
        id: 'qW6V5zYOF73N6_jtHmYX',
        defaultMessage: 'Create a full, {daysLabel} itinerary for {placeName}',
        description: 'User chat message shown when creating a full itinerary for a place with a chosen day count',
      },
      {
        daysLabel: displayDaysLabel,
        placeName,
      },
    )
    onSendMessage(
      appendContextTag(
        displayMessage,
        `Create a full, ${contextDaysLabel} itinerary for ${placeName}`,
        TRIP_PLANNER_ACTION_CONTEXT_LABEL,
      ),
    )
  }, [intl, closeCreateItineraryDropdown, itineraryDays, onSendMessage, placeName])

  const handleCreateItineraryWithNumberOfDays = useCallback(() => {
    if (numberOfDays > 0) {
      handleCreateItinerary(numberOfDays)
    }
  }, [numberOfDays, handleCreateItinerary])

  const handleCreateItineraryFromDropdown = useCallback(() => {
    handleCreateItinerary()
  }, [handleCreateItinerary])

  const handleDelete = useCallback(async() => {
    if (deleteActionsDisabled) return
    const confirmed = await showModal<boolean>(<ConfirmDeleteStopModal placeName={placeName} />)
    if (!confirmed) return
    await onDeleteTarget?.({ targetType: 'stop', stopId })
  }, [deleteActionsDisabled, showModal, placeName, onDeleteTarget, stopId])

  const handleOpenHotelDrawer = useCallback(() => {
    setIsHotelDrawerOpen(true)
  }, [])

  const handleHotelActionsClick = useCallback(() => {
    setIsHotelDrawerOpen(true)
  }, [])

  const handleCloseHotelDrawer = useCallback(() => {
    setIsHotelDrawerOpen(false)
  }, [])

  const handleOpenExperienceDrawer = useCallback(() => {
    setIsExperienceDrawerOpen(true)
  }, [])

  const handleCloseExperienceDrawer = useCallback(() => {
    setIsExperienceDrawerOpen(false)
  }, [])

  const handleShowMap = useCallback((hotel: DrawerHotel) => {
    handleCloseHotelDrawer()
    const mapViewData: HotelMapViewData = {
      searchQuery: placeName,
      offers: [{
        id: hotel.id,
        name: hotel.name,
        coordinates: hotel.geoLocation!,
      }],
    }
    onShowMap(mapViewData)
  }, [handleCloseHotelDrawer, placeName, onShowMap])

  const handleExploreMap = useCallback(() => {
    handleCloseHotelDrawer()
    if (stopCoordinates) {
      onExploreMap(stopCoordinates, 'le-hotels')
    }
  }, [handleCloseHotelDrawer, onExploreMap, stopCoordinates])

  const handleExploreExperienceMap = useCallback(() => {
    handleCloseExperienceDrawer()
    if (stopCoordinates) {
      onExploreMap(stopCoordinates, 'le-experiences')
    }
  }, [handleCloseExperienceDrawer, onExploreMap, stopCoordinates])

  const handleShowExperienceOnMap = useCallback((experience: { offerId: string; name: string; geoLocation: { lat: number; lng: number } }) => {
    handleCloseExperienceDrawer()
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [handleCloseExperienceDrawer, onShowExperienceOnMap])

  const handleHighlightClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { googlePlaceId: highlightGooglePlaceId, name, itemId } = event.currentTarget.dataset
    if (highlightGooglePlaceId && name && itemId) {
      setSelectedHighlight({ googlePlaceId: highlightGooglePlaceId, name, itemId: Number(itemId) })
    }
  }, [])

  const handleCloseHighlightDrawer = useCallback(() => {
    setSelectedHighlight(undefined)
  }, [])

  const handleCloseHotelHighlightDrawer = useCallback(() => {
    setSelectedHotelHighlight(undefined)
  }, [])

  const handleDeleteHighlightItem = useCallback((itemId: number) => {
    if (deleteActionsDisabled) return
    const highlight = highlights?.find((h) => h.id === itemId)
    if (!highlight) return
    onDeleteTarget?.({ targetType: 'item', itemId })
    setSelectedHighlight(undefined)
  }, [deleteActionsDisabled, highlights, onDeleteTarget])

  const handleShowExperienceHighlightOnMap = useCallback((experience: DrawerExperience) => {
    handleCloseHotelHighlightDrawer()
    if (!experience.geoLocation) return
    onShowExperienceOnMap?.({
      searchQuery: experience.name,
      offers: [{ id: experience.offerId, name: experience.name, coordinates: experience.geoLocation }],
    })
  }, [handleCloseHotelHighlightDrawer, onShowExperienceOnMap])

  const { sortedHighlights } = useSortedHighlights(highlights)

  const hotelHighlightHandlers = useMemo(() => {
    const handlers: Record<number, () => void> = {}

    for (const highlight of sortedHighlights) {
      if (!highlight.googlePlaceId && highlight.savedItemData) {
        const savedData = highlight.savedItemData

        if (isHotelSavedData(savedData)) {
          const searchParams: OfferClickSearchParams = {}
          if (savedData.checkIn) searchParams.checkIn = savedData.checkIn
          if (savedData.checkOut) searchParams.checkOut = savedData.checkOut

          handlers[highlight.id] = () => setSelectedHotelHighlight({
            offerId: savedData.offerId,
            accommodationPropertyId: isSppSavedData(savedData) ? savedData.accommodationPropertyId : undefined,
            offerType: savedData.offerType,
            searchParams: Object.keys(searchParams).length > 0 ? searchParams : undefined,
          })
        } else if (isExperienceSavedData(savedData)) {
          handlers[highlight.id] = () => setSelectedHotelHighlight({
            offerId: savedData.experienceId,
            offerType: 'experience',
          })
        }
      }
    }

    return handlers
  }, [sortedHighlights])

  const placeNameHeading = googlePlaceId ? (
    <ClickableHeading onClick={onLearnMoreClickHandler}>
      <Heading variant="heading5">{placeName}</Heading>
    </ClickableHeading>
  ) : (
    <Heading variant="heading5">{placeName}</Heading>
  )

  const learnMoreLabel = intl.formatMessage({
    id: '54YyE2EorGEGt1ZOGM1l',
    defaultMessage: 'Learn more',
    description: 'Button label to learn more about a trip destination',
  })

  const daysLabel = intl.formatMessage({
    id: 'AWyESid1WUYQEQ9E8MIc',
    defaultMessage: '{count, plural, one {# day} other {# days}}',
    description: 'Day count shown in the trip planner stop header subtitle',
  }, { count: numberOfDays })

  const dateRangeText = currentStop?.startDate && currentStop?.endDate
    ? formatStopDateRange(currentStop.startDate, currentStop.endDate)
    : undefined

  const stopSubtitle = [dateRangeText, numberOfDays > 0 ? daysLabel : undefined]
    .filter(Boolean)
    .join(' · ')

  const itineraryHeadingLabel = intl.formatMessage({
    id: 'RUICEpJ3xQuhVvPQHvCG',
    defaultMessage: 'Itinerary',
    description: 'Section heading for the trip stop itinerary in the trip planner',
  })

  const highlightsHeadingLabel = intl.formatMessage({
    id: 'vCnI0fSFo0TfSafwS2AE',
    defaultMessage: 'Highlights',
    description: 'Section heading for the trip stop highlights in the trip planner',
  })

  const createItineraryLabel = intl.formatMessage({
    id: 'FKx4t_HttTBgdfFBFUcB',
    defaultMessage: 'Create itinerary',
    description: 'Button label to create an itinerary for a trip stop',
  })

  const createLabel = intl.formatMessage({
    id: '4XOf32wU7_mbMlGZiToJ',
    defaultMessage: 'Create',
    description: 'Primary action label in the create itinerary dropdown',
  })

  const showHighlightFallback = !hasDays && hasHighlights
  const sectionHeadingLabel = showHighlightFallback ? highlightsHeadingLabel : itineraryHeadingLabel

  return (
    <>
      <StopSheet border="light-foreground">
        <StopSheetContent direction="vertical" gap={0}>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <StopHeaderChevronButton onClick={toggleStopExpanded}>
              <StopHeaderChevron $expanded={isStopExpanded}>
                <LineAngleDownIcon />
              </StopHeaderChevron>
            </StopHeaderChevronButton>
            {(!!imageId || isEnriching) && (
              <ImageContainer onClick={googlePlaceId ? onLearnMoreClickHandler : undefined}>
                {imageId && <ResponsiveImage aspectRatio="1:1" id={imageId} fit="center" alt={placeName} />}
                {!imageId && <PlaceImageLoader itemId={stopId} />}
              </ImageContainer>
            )}
            <StopMainContent direction="vertical" gap={2} verticalAlign="center" fullWidth>
              <Group direction="horizontal" gap={12} horizontalAlign="space-between" verticalAlign="start" fullWidth>
                <StopTitleContent direction="vertical" gap={2}>
                  {placeNameHeading}
                  {stopSubtitle && <BodyText variant="small" colour="neutral-two">{stopSubtitle}</BodyText>}
                </StopTitleContent>
                <Group direction="horizontal" gap={8} verticalAlign="start">
                  <ChatItemDropdown onDelete={handleDelete} />
                </Group>
              </Group>
              {googlePlaceId && (
                <StopLearnMoreButton size="small" kind="tertiary" onClick={onLearnMoreClickHandler}>
                  {learnMoreLabel}
                </StopLearnMoreButton>
              )}
            </StopMainContent>
          </Group>
          <StopDetailsPanel $expanded={isStopExpanded}>
            <StopDetailsContent $expanded={isStopExpanded}>
              <Group direction="vertical" gap={12}>
                <TripPlanHotelTile
                  bookedHotelSummary={bookedHotelSummary}
                  hasSavedHotels={hasSavedHotels}
                  hotelTilePrice={hotelTilePrice}
                  onActionsClick={handleHotelActionsClick}
                  onOpen={handleOpenHotelDrawer}
                  savedHotelCount={savedHotelCount}
                />
                <TripPlanExperienceTile
                  bookedExperienceSummary={bookedExperienceSummary}
                  bookedExperienceCount={bookedExperienceCount}
                  hasSavedExperiences={hasSavedExperiences}
                  experienceTilePrice={experienceTilePrice}
                  onActionsClick={handleOpenExperienceDrawer}
                  onOpen={handleOpenExperienceDrawer}
                  savedExperienceCount={savedExperienceCount}
                />
              </Group>
              {(hasHighlights || hasDays || hasItemsWithoutDay) && (
                <Sheet padding={16}>
                  <Group direction="vertical" gap={8}>
                    {!hasDays && !isGeneratingItinerary && (
                      <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between">
                        <BodyText variant="large" weight="bold">{sectionHeadingLabel}</BodyText>
                        <>
                          <TextButton
                            size="medium"
                            kind="primary"
                            shape="round"
                            startIcon={<SolidSparklesIcon />}
                            onClick={numberOfDays > 0 ? handleCreateItineraryWithNumberOfDays : toggleCreateItineraryDropdown}
                            ref={createItineraryTriggerRef}
                            disabled={isStreaming}
                          >
                            {createItineraryLabel}
                          </TextButton>
                          {!(numberOfDays > 0) && (
                            <DropdownList
                              size="S"
                              open={isCreateItineraryOpen}
                              onClose={closeCreateItineraryDropdown}
                              triggerRef={createItineraryTriggerRef}
                              anchorRef={createItineraryTriggerRef}
                              placement="bottom-end"
                              portaled
                              primaryActionProps={{ children: createLabel, onClick: handleCreateItineraryFromDropdown }}
                              footerActionsLayout="stacked"
                              enableFlip
                            >
                              <Sheet padding={16}>
                                <Group direction="vertical" gap={8} horizontalAlign="start">
                                  <BodyText variant="medium">
                                    {intl.formatMessage(
                                      {
                                        id: 'CsC8uRppEP6LyE7SIfDU',
                                        defaultMessage: 'How many days would you like to spend in <strong>{placeName}</strong>?',
                                        description: 'Body text asking user to choose number of days for creating an itinerary',
                                      },
                                      { placeName, strong: (chunks: React.ReactNode) => <strong>{chunks}</strong> },
                                    )}
                                  </BodyText>
                                  <Counter
                                    value={itineraryDays}
                                    min={1}
                                    max={365}
                                    onChange={onItineraryDaysChange}
                                    size="large"
                                  />
                                </Group>
                              </Sheet>
                            </DropdownList>
                          )}
                        </>
                      </Group>
                    )}
                    {showHighlightFallback && !isGeneratingItinerary && (
                      <Group direction="vertical" gap={8}>
                        {sortedHighlights.map((highlight) => (
                          <Group key={`${highlight.id}-${highlight.name}`} direction="horizontal" gap={8} verticalAlign="start">
                            <BodyText variant="medium" as="span">
                              {highlight.emoji}
                            </BodyText>
                            <BodyText variant="medium">
                              {highlight.googlePlaceId && (
                                <ClickableHighlightTitle
                                  data-google-place-id={highlight.googlePlaceId}
                                  data-name={highlight.name}
                                  data-item-id={highlight.id}
                                  onClick={handleHighlightClick}
                                >
                                  {highlight.name}
                                </ClickableHighlightTitle>
                              )}
                              {!highlight.googlePlaceId && hotelHighlightHandlers[highlight.id] && (
                                <ClickableHighlightTitle onClick={hotelHighlightHandlers[highlight.id]}>
                                  {highlight.name}
                                </ClickableHighlightTitle>
                              )}
                              {!highlight.googlePlaceId && !hotelHighlightHandlers[highlight.id] && <b>{highlight.name}</b>}
                              {formatDashSeparator(intl)}{highlight.description}
                            </BodyText>
                          </Group>
                        ))}
                      </Group>
                    )}
                    {isGeneratingItinerary && !hasDays && (
                      <ItineraryLoadingState daysCount={requestedDaysCount} />
                    )}
                    {(hasDays || (hasItemsWithoutDay && !showHighlightFallback)) && (
                      <DayItineraryCanvas
                        data={data}
                        stopId={stopId}
                        onSendMessage={onSendMessage}
                        onDeleteTarget={onDeleteTarget}
                        onShowMap={handleShowMap}
                        onShowExperienceOnMap={onShowExperienceOnMap}
                        onAskAI={onAskAI}
                        onAskAIForItem={onAskAIForItem}
                        scrollTargetDayId={scrollTargetDayId}
                        scrollTargetItemId={scrollTargetItemId}
                      />
                    )}
                  </Group>
                </Sheet>
              )}
            </StopDetailsContent>
          </StopDetailsPanel>
        </StopSheetContent>
      </StopSheet>
      {selectedHighlight && (
        <PlaceInfoDrawer
          isOpen
          onClose={handleCloseHighlightDrawer}
          googlePlaceId={selectedHighlight.googlePlaceId}
          placeName={selectedHighlight.name}
          itemId={selectedHighlight.itemId}
          onDelete={handleDeleteHighlightItem}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      <HotelListingDrawer
        isOpen={isHotelDrawerOpen}
        onClose={handleCloseHotelDrawer}
        placeName={placeName}
        placeCountry={placeCountry}
        stopStartDate={currentStop?.startDate}
        stopEndDate={currentStop?.endDate}
        stopCoordinates={stopCoordinates}
        contextStop={currentStop}
        onShowMap={handleShowMap}
        onExploreMap={stopCoordinates ? handleExploreMap : undefined}
        itineraryData={data}
        onSendMessage={onSendMessage}
        initialSelectedOfferId={bookedHotelSummary?.offerId}
        initialSelectedOfferType={bookedHotelSummary?.offerType}
      />
      <ExperienceListingDrawer
        isOpen={isExperienceDrawerOpen}
        onClose={handleCloseExperienceDrawer}
        placeName={placeName}
        placeCountry={placeCountry}
        contextStop={currentStop}
        stopCoordinates={stopCoordinates}
        onExploreMap={stopCoordinates ? handleExploreExperienceMap : undefined}
        itineraryData={data}
        onSendMessage={onSendMessage}
        onShowExperienceOnMap={handleShowExperienceOnMap}
        initialSelectedOfferId={bookedExperienceSummary?.offerId}
      />
      {selectedHotelHighlight?.offerType === 'experience' && (
        <ChatExperienceDetailDrawer
          offerId={selectedHotelHighlight.offerId}
          isOpen
          onClose={handleCloseHotelHighlightDrawer}
          onShowMap={handleShowExperienceHighlightOnMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
        />
      )}
      {selectedHotelHighlight && selectedHotelHighlight.offerType !== 'experience' && (
        <ChatOfferDetailDrawer
          offerId={selectedHotelHighlight.offerId}
          accommodationPropertyId={selectedHotelHighlight.accommodationPropertyId}
          offerType={selectedHotelHighlight.offerType}
          isOpen
          onClose={handleCloseHotelHighlightDrawer}
          onShowMap={handleShowMap}
          itineraryData={data}
          onSendMessage={onSendMessage}
          searchParams={selectedHotelHighlight.searchParams}
        />
      )}
    </>
  )
}

export default TripPlanCanvasItem
